// @flow

import React, { memo, useCallback, useEffect, useMemo, useRef, forwardRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { throttle } from 'throttle-debounce'
import * as Sentry from '@sentry/react'

import { useUserSettings } from '@alphaott/user-settings'
import { useVisibilityStatus } from '@alphaott/smart-tv-current-device/hooks'
import { isP2PEnabled as getP2PEnabled, getP2PConfig } from '@alphaott/smart-tv-config'
import { PLAYER_AUDIO_PARAM, PLAYER_SUBTITLES_PARAM } from '@alphaott/app-config'
import { getSmartTVVideoPlayer } from '@alphaott/app-main/selectors'

import { VideoPlayer } from './VideoPlayer'
import { useActions } from '../../store'
import { usePlaybackRecovery } from '../../hooks/usePlaybackRecovery'
import { LOAD_BLOCKED, CANCEL_TIMED_OUT, CANCEL_FAILED } from '../../utils/loadGate'
import { describePlaybackError, reportPlaybackFailure } from '../../utils/playbackDiagnostics'
import { getEngineName } from './utils'

import type { MediaStream } from '../../types'

export const UPDATE_RECENT_MOVIE_PERCENT = 600000

export const is403 = (status) => status?.code === 403
export const is404 = (status) => status?.code === 404
export const is502 = (status) => status?.code === 502

// ShakaPlayer;
export const isBadHttpStatusError = (error) => error?.code === 1001
export const isHttpError = (error) => error?.code === 1002
export const isTimeoutError = (error) => error?.code === 1003

// VideoJS;
export const isNoCompatibleSourceError = (error) => error?.code === 4

// Errors the engine may still get over while the playback keeps moving
// eslint-disable-next-line complexity
export const isTransientError = (error: Object): boolean =>
  isTimeoutError(error) ||
  isHttpError(error) ||
  isBadHttpStatusError(error) ||
  is404(error?.status) ||
  isNoCompatibleSourceError(error)

// Raised by the adapters themselves: the user gets the general text without a code
const RECOVERY_ERROR_CODES = [LOAD_BLOCKED, CANCEL_TIMED_OUT, CANCEL_FAILED]

type VideoPlayerContainerProps = {
  videoId: string,
  contentTitle?: string,
  contentType?: string,
  mediaStreams: MediaStream[],
  autoplay?: boolean,
  defaultTimePosition?: number,
  onTimeUpdate: (currentTime: number, duration: number) => void,
  onThrottledTimeUpdate: (currentTime: number, duration: number) => void,
  onShowLimitError: () => void,
}

const VideoPlayerContainerPure = (
  {
    videoId,
    contentTitle,
    contentType,
    mediaStreams,
    autoplay,
    defaultTimePosition,
    onTimeUpdate,
    onThrottledTimeUpdate,
    onShowLimitError,
  }: VideoPlayerContainerProps,
  playerRef: any,
) => {
  const navigate = useNavigate()

  const { visibilityStatus, isVisible, prevStatus: prevVisibilityStatus } = useVisibilityStatus()
  const { changePreferredTrack } = useUserSettings()
  const {
    onLoadMeta,
    onCanPlay,
    onLoading,
    onSeeking,
    onSeeked,
    onPlay,
    onPause,
    onEnd,
    onSourceLoaded,
    onReset,
    onChangeAudioTracks,
    onChangeSubtitleTracks,
  } = useActions()

  const isP2PEnabled = useSelector(getP2PEnabled)
  const p2pConfig = useSelector(getP2PConfig)
  const engine = getEngineName(useSelector(getSmartTVVideoPlayer))

  const resolvedMediaStreams = useMemo(
    () =>
      (mediaStreams || []).map((stream) => ({
        ...stream,
        p2p: isP2PEnabled || stream?.p2p === true,
      })),
    [isP2PEnabled, mediaStreams],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleThrottledTimeUpdate = useCallback(
    throttle(UPDATE_RECENT_MOVIE_PERCENT, onThrottledTimeUpdate, { noTrailing: false }),
    [onThrottledTimeUpdate],
  )

  const handleTimeUpdate = useCallback(
    (time) => {
      if (onTimeUpdate) {
        onTimeUpdate(time)
      }

      handleThrottledTimeUpdate(time)
    },
    [onTimeUpdate, handleThrottledTimeUpdate],
  )

  const handleChangeAudioTracks = useCallback(
    (
      audioTrack: Object,
      audioTracks: Object[],
      options: {
        preferred?: boolean,
      },
      // eslint-disable-next-line consistent-return
    ) => {
      if (options?.preferred) {
        return changePreferredTrack({
          videoId,
          option: PLAYER_AUDIO_PARAM,
          track: audioTrack.language,
        })
      }

      onChangeAudioTracks(audioTracks)
    },
    [videoId, onChangeAudioTracks, changePreferredTrack],
  )

  const handleChangeSubtitleTracks = useCallback(
    (
      subtitleTrack: Object,
      subtitleTracks: Object[],
      options: {
        preferred?: boolean,
      },
      // eslint-disable-next-line consistent-return
    ) => {
      if (options?.preferred) {
        return changePreferredTrack({
          videoId,
          option: PLAYER_SUBTITLES_PARAM,
          track: subtitleTrack?.language,
        })
      }

      onChangeSubtitleTracks(subtitleTracks)
    },
    [videoId, onChangeSubtitleTracks, changePreferredTrack],
  )

  // One Sentry event per failed cycle instead of one per engine error
  const handleFailure = useCallback(
    (failure) => {
      reportPlaybackFailure({ ...failure, engine, contentId: videoId, contentTitle, contentType })
    },
    [engine, videoId, contentTitle, contentType],
  )

  // The engine's own handle; the screen gets the same one with Play routed through the recovery
  const engineRef = useRef(null)

  const recovery = usePlaybackRecovery({
    playerRef: engineRef,
    mediaStreams,
    defaultTimePosition,
    isVisible,
    onFailure: handleFailure,
  })

  // What the error tells the user: a status text of its own (shown as is), the engine's message or
  // code (the modal puts it into a localized sentence), or null when it tells nothing (no message
  // or code, internal stop and load errors): the modal then shows the localized general text
  const handleError = useCallback(
    // eslint-disable-next-line complexity,consistent-return
    (error) => {
      if (error?.status?.message && !error?.status?.message?.toLowerCase?.()?.includes('<html>')) {
        return { message: error.status.message }
      }

      const code = RECOVERY_ERROR_CODES.includes(error?.code) ? null : error?.message || error?.code

      return code ? { code } : null
    },
    [],
  )

  const { onPlayerError, onRecordError } = recovery

  const onPlayRequestRef = useRef(recovery.onPlayRequest)
  onPlayRequestRef.current = recovery.onPlayRequest

  // Every Play of the screens (button click, OK) comes through the handle's onPlay. After the error
  // modal was closed the source is unloaded, so Play starts a new cycle; otherwise the engine plays
  const handleEngineRef = useCallback(
    // eslint-disable-next-line complexity
    (handle) => {
      engineRef.current = handle

      const screenHandle = handle && {
        ...handle,
        onPlay: (...args) => (onPlayRequestRef.current() ? undefined : handle.onPlay?.(...args)),
      }

      if (typeof playerRef === 'function') {
        playerRef(screenHandle)
      } else if (playerRef) {
        // eslint-disable-next-line no-param-reassign
        playerRef.current = screenHandle
      }
    },
    [playerRef],
  )

  // Stable between renders, so the adapters always reach the current handlers
  const handlePlayerError = useCallback(
    // eslint-disable-next-line consistent-return,complexity
    (error) => {
      if (is403(error?.status)) {
        return onShowLimitError()
      }

      const details = describePlaybackError(error)

      // Not shown and not reported on its own; kept for the final event and, should the cycle
      // fail, for the text of the modal
      if (error?.severity === 1) {
        return onRecordError(details, handleError(error))
      }

      // Не уверен что нужен этот костыль, будем тестировать;
      if (is502(error?.status) || error?.message?.includes('502 Bad Gateway')) {
        return onRecordError(details, handleError(error))

        // setTimeout(() => {
        //   if (not(isPaused)) {
        //     onClearErrors()
        //   }
        // }, 10000)
      }

      // The recovery decides: an error while the playback keeps moving waits for a stall
      onPlayerError(handleError(error), { isTransient: isTransientError(error), details })
    },
    [onShowLimitError, onPlayerError, onRecordError, handleError],
  )

  const handleDestroy = useCallback(
    (time) => {
      onThrottledTimeUpdate(time)
    },
    [onThrottledTimeUpdate],
  )

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (isVisible && prevVisibilityStatus?.isHidden) {
      navigate(-1)

      Sentry.withScope((scope) => {
        scope.setTag('eventType', 'visibility_playback_exit')

        scope.addBreadcrumb({
          category: 'visibility',
          message: 'Visibility restored on playback, navigating back',
          level: 'info',
        })

        scope.setContext('visibility', {
          previousState: 'hidden',
          currentState: 'visible',
          screen: 'playback',
          videoId,
        })

        Sentry.captureMessage(
          'Playback screen exited after TV power-on (visibility restored)',
          'info',
        )
      })
    }
  }, [visibilityStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      onReset()
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <VideoPlayer
      videoId={videoId}
      ref={handleEngineRef}
      mediaStreams={resolvedMediaStreams}
      autoplay={autoplay}
      defaultTimePosition={defaultTimePosition}
      p2pConfig={p2pConfig}
      onLoadMeta={onLoadMeta}
      onCanPlay={onCanPlay}
      onLoading={onLoading}
      onSeeking={onSeeking}
      onSeeked={onSeeked}
      onPlay={onPlay}
      onPause={onPause}
      onEnd={onEnd}
      onSourceLoaded={onSourceLoaded}
      onTimeUpdate={handleTimeUpdate}
      onError={handlePlayerError}
      onChangeAudioTracks={handleChangeAudioTracks}
      onChangeSubtitleTracks={handleChangeSubtitleTracks}
      onDestroy={handleDestroy}
      onRetry={recovery.onRetry}
      onDismiss={recovery.onDismiss}
    />
  )
}

export const VideoPlayerContainer = memo<VideoPlayerContainerProps>(
  forwardRef(VideoPlayerContainerPure),
)

export default VideoPlayerContainer
